/**
 * Pure helpers used by the Company Admin Dashboard (and any other
 * page that wants the same formatters). Extracted from
 * `pages/app/DashboardPage.jsx` so they can be unit-tested without
 * a JSX/DOM toolchain — Node's built-in `node:test` runner can
 * import this plain JS module directly.
 *
 * All functions are pure and side-effect-free except `timeOfDay()`
 * which reads `new Date()` and `fmtRelative()` which reads
 * `Date.now()`. Both accept no arguments and are isolated for that
 * reason — tests stub the `Date` global when they need to fix time.
 */

import {
  datePattern,
  formatDateTime,
  formatNumber,
  formatPattern,
  getDisplayTimeZone,
} from './format.js'

/**
 * Pull the first word out of a full name. Used by the dashboard's
 * "Good morning, {first}" greeting. Returns the fallback string
 * `'there'` for nullish, empty, or whitespace-only input.
 */
export function firstName(full) {
  return full?.trim().split(/\s+/)[0] ?? 'there'
}

/**
 * FULL display name for the "Good morning, {name}" greeting. Trims and
 * collapses any run of whitespace to a single space, so a name typed with a
 * stray double space (e.g. "Komal-  Dumbre") still renders the whole name
 * cleanly instead of the greeting showing only the first word. Falls back to
 * 'there' for nullish / empty / whitespace-only input.
 */
export function greetingName(full) {
  const name = String(full ?? '').trim().replace(/\s+/g, ' ')
  return name.length > 0 ? name : 'there'
}

/**
 * Time-of-day label for the dashboard greeting. Reads the current
 * hour and bucketed into Morning (< 12) / Afternoon (< 17) /
 * Evening (≥ 17). The Focus Board's `timeOfDay` has a 4th "Night"
 * bucket; the dashboard's simpler 3-way split is intentional —
 * fewer transitions per day so the greeting feels less twitchy.
 *
 * The hour is read in the person's display timezone (their own zone,
 * else the company's) so the greeting matches the header clock; with
 * no zone set it is the browser's local hour.
 */
export function timeOfDay() {
  const zone = getDisplayTimeZone()
  const h = zone ? Number(formatPattern(new Date(), 'H')) : new Date().getHours()
  if (h < 12) return 'Morning'
  if (h < 17) return 'Afternoon'
  return 'Evening'
}

/**
 * Compact "Xh Ym" formatter for minute counts. Used for time-tracked
 * totals across the dashboard. Rules:
 *   - < 60 min → "{m}m"
 *   - whole hours (no remainder) → "{h}h"
 *   - mixed → "{h}h {r}m"
 */
export function fmtMinutes(m) {
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  const r = m % 60
  if (r === 0) return `${h}h`
  return `${h}h ${r}m`
}

/**
 * Two-letter avatar initials from a full name. "Mayur Auti" → "MA".
 * Single-name input returns just the first character. Falls back to
 * "?" for empty / nullish input so the UI doesn't render a blank
 * circle.
 */
export function initials(name) {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?'
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase()
}

/**
 * Human-readable relative-time string ("3 minutes ago", "2 days
 * ago"). Falls back to the canonical "DD/MM/YYYY, HH:MM AM/PM"
 * absolute timestamp (via `formatDateTime` in `lib/format.js`) once
 * the gap exceeds a week, so older items keep date *and* time of day.
 *
 * Inputs:
 *   - `null` / `undefined` / `''` → "—"
 *   - any value `new Date()` accepts (ISO string, ms, etc.)
 */
export function fmtRelative(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.round(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH} ${diffH === 1 ? 'hour' : 'hours'} ago`
  const diffD = Math.round(diffH / 24)
  if (diffD < 7) return `${diffD} ${diffD === 1 ? 'day' : 'days'} ago`
  return formatDateTime(d)
}

/**
 * Bytes → "12.4 GB" / "850 MB" / "3 KB" / "0 B". Used by the
 * Storage By File Type donut on the dashboard and the Storage page
 * (Platform Admin) — same shape as `fmtBytes` in PlatformStoragePage.
 */
export function formatBytes(bytes) {
  const v = Number(bytes ?? 0)
  if (v === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(units.length - 1, Math.floor(Math.log(v) / Math.log(1024)))
  const scaled = v / Math.pow(1024, i)
  const digits = scaled < 10 && i > 0 ? 1 : 0
  return `${formatNumber(scaled, { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false })} ${units[i]}`
}

/** Day + month only, in the order of the person's date format — "08/10"
 *  (dmy, the default), "10/08" (mdy), "10-08" (iso), "8 Oct" (long). For
 *  trend-chart X-axis labels of "YYYY-MM-DD" bucket days; a calendar day is
 *  never zone-shifted. Falls back to the raw value if it can't be parsed. */
const DAY_MONTH_PATTERNS = { 'dd/MM/yyyy': 'dd/MM', 'MM/dd/yyyy': 'MM/dd', 'yyyy-MM-dd': 'MM-dd' }
export function formatDayMonth(day) {
  if (!day) return ''
  return formatPattern(day, DAY_MONTH_PATTERNS[datePattern()] ?? 'd MMM') || String(day)
}

/**
 * Guarded week-over-week delta for a KPI trend pill. Returns a small
 * descriptor the UI renders, or `null` when there is nothing honest to show.
 * Deliberately avoids the misleading "+100% / No change" chips that were
 * pulled from the dashboards earlier — those fired against a zero baseline
 * on fresh tenants, where every change reads as +100%:
 *
 *   both periods 0        → null              (say nothing)
 *   prior 0, current > 0  → { kind: 'new' }   (not a bogus +100%)
 *   equal, non-zero       → { kind: 'flat' }
 *   otherwise             → { kind: 'up' | 'down', pct }   (pct = |rounded %|)
 *
 * `current` / `previous` are the two period totals (e.g. completed this
 * week vs last week). Nullish is treated as 0.
 */
export function weekDelta(current, previous) {
  const c = Number(current ?? 0)
  const p = Number(previous ?? 0)
  if (c === 0 && p === 0) return null
  if (p === 0) return { kind: 'new' }
  const pct = Math.round(((c - p) / p) * 100)
  if (pct === 0) return { kind: 'flat' }
  return { kind: pct > 0 ? 'up' : 'down', pct: Math.abs(pct) }
}

/**
 * Mutually-exclusive split of `tasks.total` (all tasks in scope, incl.
 * subtasks) used by the Task Completion 3D pie and the Task Health donut:
 * Completed (completedAt set) + Overdue (open AND past due) + On track (open,
 * not past due). The three admin-overview counts share one population, so
 * the parts sum to the total. (Summing openCount — the first-column "To Do"
 * count — instead would skip in-progress tasks and double-count overdue
 * tasks still in To Do.)
 */
export function taskHealthPartition(tasks) {
  const completed = Number(tasks?.completedCount ?? 0)
  const overdue = Number(tasks?.overdueCount ?? 0)
  const onTrack = Math.max(0, Number(tasks?.total ?? 0) - completed - overdue)
  return { completed, overdue, onTrack }
}

/** Fallback for a status with no workflow colour (the server's "Other"
 *  bucket) — a theme token so it reads in light and dark mode. */
export const STATUS_FALLBACK_COLOR = 'var(--color-ink-muted)'

/**
 * Shape the admin overview's `tasks.statusTrend` (tasks created each of the
 * last 14 days, split by their CURRENT workflow status) for the "Tasks by
 * Status" card on BOTH the Company Admin and Sub Admin dashboards, so the
 * two can't drift.
 *
 *   labels  — the 14 day keys
 *   series  — TrendChart series; all-zero statuses are left out of the
 *             chart (nothing to draw) but kept in the legend
 *   legend  — one chip per status in workflow order: { name, color, last7 }
 *             where last7 = tasks created in the last 7 days now in it
 *
 * Colours come from the workflow as-is; nothing is hardcoded per status.
 */
export function statusTrendSeries(statusTrend) {
  const rows = Array.isArray(statusTrend) ? statusTrend : []
  const labels = (rows[0]?.sparkline ?? []).map((p) => p.day)
  const withColor = rows.map((r) => ({
    name: r.name,
    color: r.color || STATUS_FALLBACK_COLOR,
    data: (r.sparkline ?? []).map((p) => Number(p.count ?? 0)),
  }))
  return {
    labels,
    series: withColor.filter((s) => s.data.some((v) => v > 0)),
    legend: withColor.map((s) => ({
      name: s.name,
      color: s.color,
      last7: s.data.slice(-7).reduce((sum, v) => sum + v, 0),
    })),
  }
}
